/**
 * What a cost line rests on when it is not a supplier invoice.
 *
 * Two things can stand in for an invoice at the lock point: the standard rate from the rate
 * card, and a figure a super admin set by hand. Downstream they are the same kind of thing -
 * both are estimates, both sit inside landed cost, and both have to be reversed when the
 * real invoice turns up - so which sources count lives here rather than as the bare string
 * 'standard_cost' written out in several files that can drift apart.
 *
 * The drift was not hypothetical. The split-invoice form looked for open estimates with
 * estimate_source = 'standard_cost' only; an override would have sat beside the arriving
 * invoice instead of being reconciled against it, and the vehicle would have carried the
 * freight twice - once as the override and once as the bill. That is the same double-count
 * the commission spec's own worked example warns about.
 */

/** Written by the rate card. */
export const ESTIMATE_SOURCE_STANDARD = 'standard_cost'

/**
 * Written by a super admin, by hand, with a reason.
 *
 * Nick, 12 Sep: freight has to be taken into account when commission is calculated, and the
 * super admin can override it. Without a rate card there is nothing defensible to accrue, so
 * the vehicle blocks; a named person putting a number against the car with a reason is a
 * defence, where a silent zero is not.
 */
export const ESTIMATE_SOURCE_OVERRIDE = 'super_admin_override'

export const ACCRUAL_SOURCES = [ESTIMATE_SOURCE_STANDARD, ESTIMATE_SOURCE_OVERRIDE] as const
export type AccrualSource = (typeof ACCRUAL_SOURCES)[number]

/**
 * Is this line an accrual - a figure standing in for an invoice that has not arrived?
 *
 * Case-sensitive on purpose: these are values this codebase writes, not anything typed in.
 */
export function isAccrualSource(source?: string | null): boolean {
  return (ACCRUAL_SOURCES as readonly string[]).includes(String(source || ''))
}

/** Set by a person rather than by the rate card, and therefore carrying a reason. */
export function isOverrideSource(source?: string | null): boolean {
  return String(source || '') === ESTIMATE_SOURCE_OVERRIDE
}
